import SupabaseDatabaseAdapter from "@mixitone/oom/adapters/SupabaseDatabaseAdapter";
import type {
  InsertQuery,
  Query,
  UpdateQuery,
  CountResponse,
  DataResponse,
  SingleResponse,
} from "@mixitone/oom";
import { createCrossSupabase } from "../supabaseClient";

/**
 * A database adapter that extends SupabaseDatabaseAdapter but re-instantiates
 * the Supabase client before each database call to ensure it has the latest
 * access token from Tauri. This is necessary because we don't have access to
 * the refresh token and cannot refresh it ourselves.
 */
export class TauriSupabaseDatabaseAdapter extends SupabaseDatabaseAdapter {
  /**
   * Get a fresh Supabase client with the latest access token from Tauri
   * and create a temporary adapter instance for the operation
   */
  private async getFreshAdapter(): Promise<SupabaseDatabaseAdapter> {
    const supabase = await createCrossSupabase();
    return new SupabaseDatabaseAdapter(supabase);
  }

  override async execute<Data>(query: Query<any, any>): Promise<DataResponse<Data>> {
    const adapter = await this.getFreshAdapter();
    return adapter.execute(query);
  }

  override async count(query: Query<any, any>): Promise<CountResponse> {
    const adapter = await this.getFreshAdapter();
    return adapter.count(query);
  }

  override async insert<Data>(
    query: InsertQuery<any, any>,
    data: Record<string, any>,
  ): Promise<SingleResponse<Data>> {
    const adapter = await this.getFreshAdapter();
    return adapter.insert(query, data);
  }

  override async update<Data>(
    query: UpdateQuery<any, any>,
    data: Record<string, any>,
  ): Promise<DataResponse<Data>> {
    const adapter = await this.getFreshAdapter();
    return adapter.update(query, data);
  }

  override async delete(query: UpdateQuery<any, any>): Promise<CountResponse> {
    const adapter = await this.getFreshAdapter();
    return adapter.delete(query);
  }
}
